import React, { useState } from 'react';
import { Row, Col, Card, Button, Form, Spinner, Badge, ProgressBar } from 'react-bootstrap';
import { FaBolt, FaSync, FaChartLine } from 'react-icons/fa';

export default function AdminContentEngine() {
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);

  // Form State
  const [topic, setTopic] = useState('');
  const [tone, setTone] = useState('informative');
  const [count, setCount] = useState(3);

  const handleGenerate = (e) => {
    e.preventDefault();
    setGenerating(true);
    setProgress(0);
    setResults(null);

    // Simulate AI generation pipeline (V1 concept)
    let currentProgress = 0;
    const interval = setInterval(() => {
      currentProgress += 15;
      if (currentProgress >= 100) {
        clearInterval(interval);
        setProgress(100);
        setTimeout(() => {
          setGenerating(false);
          setResults([
            { id: 1, title: `Top 5 Tips for ${topic || 'Spring Bass Fishing'}`, status: 'Draft' },
            { id: 2, title: `How to maintain your electronics for ${topic || 'fishing'}`, status: 'Draft' },
            { id: 3, title: `Understanding the habits of bass during ${topic || 'the spawn'}`, status: 'Draft' }
          ]);
        }, 500);
      } else {
        setProgress(currentProgress);
      }
    }, 600);
  };

  return (
    <div className="py-4 community-admin-container">
      <div className="mb-4">
        <h2><FaBolt className="me-2 text-warning" /> Content Generation Engine</h2>
        <p className="text-muted">Use AI to generate starter threads, FAQs, or engagement posts to keep the community active.</p>
      </div>

      <Row>
        <Col lg={7}>
          <Card className="shadow-sm border-0 mb-4">
            <Card.Header className="bg-white fw-bold py-3">Generate Content</Card.Header>
            <Card.Body>
              <Form onSubmit={handleGenerate}>
                <Form.Group className="mb-3">
                  <Form.Label>Topic / Keywords</Form.Label>
                  <Form.Control 
                    type="text" 
                    placeholder="e.g. Sonar setup, Fall transition, Boat winterization"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    required
                  />
                </Form.Group>
                
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Tone</Form.Label>
                      <Form.Select value={tone} onChange={(e) => setTone(e.target.value)}>
                        <option value="informative">Informative / Educational</option>
                        <option value="discussion">Discussion Starter (Asking questions)</option>
                        <option value="controversial">Controversial (Hot takes)</option>
                        <option value="humorous">Humorous / Casual</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Number of Posts</Form.Label>
                      <Form.Select value={count} onChange={(e) => setCount(parseInt(e.target.value))}>
                        <option value="1">1 Post</option>
                        <option value="3">3 Posts</option>
                        <option value="5">5 Posts</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-grid mt-4">
                  <Button variant="warning" type="submit" disabled={generating}>
                    {generating ? (
                      <>
                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <FaBolt className="me-2" /> Generate Content Drafts
                      </>
                    )}
                  </Button>
                </div>
              </Form>

              {generating && (
                <div className="mt-4">
                  <p className="small text-muted mb-1 text-center">AI is writing content...</p>
                  <ProgressBar animated now={progress} variant="warning" />
                </div>
              )}
            </Card.Body>
          </Card>

          {results && (
            <Card className="shadow-sm border-0 border-top border-warning border-3">
              <Card.Header className="bg-white fw-bold py-3 d-flex justify-content-between align-items-center">
                <span>Generated Drafts</span>
                <Badge bg="success">{results.length} ready</Badge>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="list-group list-group-flush">
                  {results.map(res => (
                    <div key={res.id} className="list-group-item p-3 d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="mb-1">{res.title}</h6>
                        <span className="small text-muted">Status: {res.status}</span>
                      </div>
                      <Button variant="outline-primary" size="sm">Review & Publish</Button>
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          )}
        </Col>

        <Col lg={5}>
          <Card className="shadow-sm border-0 bg-light">
            <Card.Header className="bg-light fw-bold py-3"><FaChartLine className="me-2 text-primary" /> Content Insights</Card.Header>
            <Card.Body>
              <p className="small text-muted mb-4">
                The AI analyzes community activity to suggest topics that need more engagement.
              </p>
              
              <h6 className="mb-3">Trending Gaps (Low Content)</h6>
              <div className="d-flex flex-wrap gap-2 mb-4">
                <Badge bg="secondary" className="p-2 cursor-pointer" onClick={() => setTopic('LiveScope setup')}>LiveScope setup +</Badge>
                <Badge bg="secondary" className="p-2 cursor-pointer" onClick={() => setTopic('Lake Fork patterns')}>Lake Fork patterns +</Badge>
                <Badge bg="secondary" className="p-2 cursor-pointer" onClick={() => setTopic('Prop selection')}>Prop selection +</Badge>
              </div>
              
              <h6 className="mb-3">Recent SEO Opportunities</h6>
              <ul className="small text-muted ps-3 mb-0">
                <li className="mb-2">"Best bass boat for rough water" (High search volume, low forum coverage)</li>
                <li className="mb-2">"How to clean marine vinyl" (Consistent seasonal searches)</li>
                <li>"Trolling motor battery types" (Highly requested in DMs)</li>
              </ul>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
